<script setup lang="ts">
import { Head, Link, router } from '@inertiajs/vue3'
import PageHeading from "@/Components/Headings/PageHeading.vue"
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import Icon from '@/Components/Utils/Icon.vue'
import Button from '@/Components/Utils/Button.vue'
import { useFormatTime } from '@/Composables/useFormatTime'
import { notify } from '@kyvg/vue3-notification'
import { computed, inject, onMounted, onUnmounted, ref } from 'vue'
import { layoutStructure } from '@/Composables/useLayoutStructure'
import InformationIcon from '@/Components/Utils/InformationIcon.vue'

const layout = inject('layout', layoutStructure)


const props = defineProps<{
    pageHead?: PageHeadingTS
    title?: string
    data: {
        id: number
        slug: string
        reference: string
        state: string
        state_label: string
        state_icon: { icon: string, class: string, tooltip: string }
        is_in_process: boolean
        has_damage: boolean
        order: {
            id: number
            slug: string
            reference: string
            type: string
            type_icon: { icon: string, class: string, tooltip: string }
            quantity: number
            product_snapshot: {
                name: string
                code: string
                price: number
                type: string
                dimension_width: number | null
                dimension_height: number | null
            } | null
            customer: { id: number, name: string, slug: string } | null
            file_merek: string | null
            note: string | null
        }
        product_name: string | null
        started_by: { id: number, name: string }
        started_at: string
        finished_by: { id: number, name: string } | null
        finished_at: string | null
        damage_quantity: number | null
        damage_note: string | null
    }
    root_active: string
}>()

const isFinishModalOpen = ref(false)
const damageQuantity = ref<number | null>(null)
const damageNote = ref('')
const useRecycle = ref(false)
const stockQuantity = ref<number>(0)

const isFlexo = props.data.order.type === 'flexo'

const orderTypeTheme = computed(() => {
    switch (props.data.order.type) {
        case 'flexo':
            return {
                name: 'Flexo',
                card: 'border-blue-400 bg-blue-50/30',
                header: 'bg-blue-50',
                badge: 'bg-blue-100 border border-blue-300 text-blue-700',
                iconWrap: 'bg-blue-100 text-blue-700 ring-1 ring-blue-300',
                focusRing: 'focus:ring-blue-400',
                radioAccent: 'accent-blue-500',
            }
        case 'seng':
            return {
                name: 'Seng',
                card: 'border-amber-400 bg-amber-50/30',
                header: 'bg-amber-50',
                badge: 'bg-amber-100 border border-amber-300 text-amber-700',
                iconWrap: 'bg-amber-100 text-amber-700 ring-1 ring-amber-300',
                focusRing: 'focus:ring-amber-400',
                radioAccent: 'accent-amber-500',
            }
        default:
            return {
                name: props.data.order.type || 'Order',
                card: 'border-sky-400 bg-sky-50/30',
                header: 'bg-sky-50',
                badge: 'bg-sky-100 border border-sky-300 text-sky-700',
                iconWrap: 'bg-sky-100 text-sky-700 ring-1 ring-sky-300',
                focusRing: 'focus:ring-sky-400',
                radioAccent: 'accent-sky-500',
            }
    }
})

const openFinishModal = () => {
    damageQuantity.value = null
    damageNote.value = ''
    useRecycle.value = false
    stockQuantity.value = props.data.order.quantity
    isFinishModalOpen.value = true
}

const isLoadingSubmitFinish = ref(false)
const errorFinishList = ref<{}>({})
const submitFinish = () => {
    router.patch(route('manufacture.manufactures.finish', props.data.id), {
        damage_quantity: damageQuantity.value ?? null,
        damage_note: damageNote.value || null,
        use_recycle: isFlexo ? useRecycle.value : false,
        stock_quantity: isFlexo && !useRecycle.value ? stockQuantity.value : null,
    }, {
        onStart: () => isLoadingSubmitFinish.value = true,
        onFinish: () => isLoadingSubmitFinish.value = false,
        onError: (errors) => {
            errorFinishList.value = errors
            notify({ title: 'Gagal', text: 'Gagal menyelesaikan produksi. Silakan coba lagi.', type: 'error' })
        },
        onSuccess: () => {
            isFinishModalOpen.value = false
            notify({ title: 'Berhasil', text: 'Produksi selesai!', type: 'success' })
        },
    })
}

onMounted(() => {
    layout.root_active = props.root_active ?? ''
})

onUnmounted(() => {
    layout.root_active = ''
})
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead">
        <template #button-finish-manufacture="{ action }">
            <Button
                @click="openFinishModal"
                :style="action.style"
                :label="action.label"
                :icon="action.icon"
            />
        </template>
    </PageHeading>

    <div class="mx-8 max-w-2xl mt-6 space-y-6">
        <!-- Status badge -->
        <div class="flex flex-wrap items-center gap-x-2 gap-y-2">
            <span class="inline-flex items-center gap-x-1.5 rounded px-2 py-1 text-xs font-semibold uppercase tracking-wide" :class="orderTypeTheme.badge">
                <Icon :data="data.order.type_icon" />
                {{ orderTypeTheme.name }}
            </span>
            <span class="inline-flex items-center gap-x-1.5 text-sm font-medium">
                <Icon :data="data.state_icon" />
                {{ data.state_label }}
            </span>
        </div>

        <!-- Detail card -->
        <div class="w-full overflow-hidden rounded-xl border" :class="orderTypeTheme.card">
            <div class="flex flex-col gap-y-1 border-b border-gray-900/5 p-6" :class="orderTypeTheme.header">
                <div class="flex items-center gap-x-2 font-bold text-lg">
                    <span class="inline-flex size-8 items-center justify-center rounded-full" :class="orderTypeTheme.iconWrap">
                        <Icon :data="data.order.type_icon" />
                    </span>
                    <span>{{ data.reference }}</span>
                </div>
                <div v-tooltip="'Tgl. mulai produksi'" class="text-sm text-gray-500 w-fit">
                    {{ useFormatTime(data.started_at) }}
                </div>
            </div>

            <dl class="-my-3 divide-y divide-gray-100 px-6 py-4 text-sm/6">
                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Jenis Order</dt>
                    <dd>
                        <span class="inline-flex items-center gap-x-1.5 rounded px-2 py-1 text-xs font-semibold uppercase tracking-wide" :class="orderTypeTheme.badge">
                            <Icon :data="data.order.type_icon" />
                            {{ orderTypeTheme.name }}
                        </span>
                    </dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">No. Order</dt>
                    <dd>
                        <Link :href="route('casheer.orders.show', data.order.slug)" class="primaryLink font-medium">
                            {{ data.order.reference }}
                        </Link>
                    </dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Produk</dt>
                    <dd class="font-medium">{{ data.order?.product_snapshot?.name ?? '-' }}</dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Jumlah produk yang dipesan</dt>
                    <dd class="font-medium">{{ data.order.quantity }}</dd>
                </div>

                <div v-if="data.order?.type === 'flexo'" class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Dimensi (W × H)</dt>
                    <dd class="font-medium">{{ data.order?.product_snapshot?.dimension_width }} × {{ data.order?.product_snapshot?.dimension_height }} cm</dd>
                </div>

                <div class="flex justify-between gap-x-6 py-3">
                    <dt class="text-gray-500 whitespace-nowrap">File merek</dt>
                    <dd class="font-medium text-right">{{ data.order?.file_merek ?? '-' }}</dd>
                </div>

                <div class="flex flex-col gap-x-6 py-3">
                    <dt class="text-gray-500 whitespace-nowrap">
                        Catatan dari Order <InformationIcon class="ml-1" :information="'Catatan dibuat oleh Layout atau Kasir.'" />
                    </dt>
                    <dd class="xfont-medium text-right border rounded px-2 py-1 bg-gray-200/50">{{ data.order?.note ?? '-' }}</dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Pelanggan</dt>
                    <dd class="font-medium">{{ data.order.customer?.name ?? '-' }}</dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Dikerjakan oleh</dt>
                    <dd class="font-medium">{{ data.started_by?.name ?? '-' }}</dd>
                </div>

                <template v-if="!data.is_in_process">
                    <div class="flex justify-between gap-x-4 py-3">
                        <dt class="text-gray-500">Diselesaikan oleh</dt>
                        <dd class="font-medium">{{ data.finished_by?.name ?? '-' }}</dd>
                    </div>

                    <div class="flex justify-between gap-x-4 py-3">
                        <dt class="text-gray-500">Tgl. Selesai</dt>
                        <dd class="font-medium">{{ data.finished_at ? useFormatTime(data.finished_at) : '-' }}</dd>
                    </div>
                </template>

                <template v-if="data.has_damage">
                    <div class="flex justify-between gap-x-4 py-3">
                        <dt class="text-red-600">Jumlah Kerusakan</dt>
                        <dd class="font-medium text-red-600">{{ data.damage_quantity }}</dd>
                    </div>

                    <div class="flex justify-between gap-x-4 py-3">
                        <dt class="text-red-600">Keterangan Kerusakan</dt>
                        <dd class="font-medium">{{ data.damage_note }}</dd>
                    </div>
                </template>
            </dl>
        </div>
    </div>

    <!-- Modal: Selesai -->
    <Teleport to="body">
        <div v-if="isFinishModalOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div class="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4 space-y-4">
                <h3 class="font-bold text-lg">Selesaikan Produksi</h3>
                <p class="text-sm text-gray-500">
                    Isi jumlah kerusakan jika ada produk yang rusak selama produksi.<br>
                    Kosongkan jika tidak ada kerusakan.
                </p>

                <div class="space-y-3">
                    <!-- Flexo: opsi recycle/kurangi stok -->
                    <div v-if="isFlexo" class="space-y-2">
                        <label class="block text-sm font-medium text-gray-700">Penggunaan Stok</label>
                        <div class="flex flex-col gap-1.5">
                            <label class="flex items-center gap-2 cursor-pointer">
                                <input type="radio" v-model="useRecycle" :value="false" :class="orderTypeTheme.radioAccent" />
                                <span class="text-sm">Kurangi stok</span>
                            </label>
                            <label class="flex items-center gap-2 cursor-pointer">
                                <input type="radio" v-model="useRecycle" :value="true" :class="orderTypeTheme.radioAccent" />
                                <span class="text-sm">Gunakan recycle sepenuhnya <span class="text-gray-400">(stok tidak berkurang)</span></span>
                            </label>
                        </div>

                        <div v-if="!useRecycle">
                            <div class="flex items-center justify-between mb-1">
                                <label class="block text-sm font-medium text-gray-700">
                                    Jumlah Stok yang Dikurangi
                                </label>
                                <button
                                    type="button"
                                    :disabled="stockQuantity === data.order.quantity"
                                    class="text-xs underline text-blue-500 hover:text-blue-600 disabled:text-gray-300 disabled:cursor-not-allowed"
                                    @click="stockQuantity = data.order.quantity"
                                >
                                    Samakan dengan jumlah order ({{ data.order.quantity }})
                                </button>
                            </div>
                            <input
                                v-model="stockQuantity"
                                type="number"
                                min="0"
                                class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2"
                                :class="orderTypeTheme.focusRing"
                            />
                        </div>
                    </div>

                    <div class="!mt-4">
                        <label class="block text-sm font-medium text-red-500 mb-1">
                            Jumlah Kerusakan <span class="opacity-60">(opsional)</span>
                        </label>
                        <input
                            v-model="damageQuantity"
                            type="number"
                            min="0"
                            placeholder="0"
                            class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2"
                            :class="orderTypeTheme.focusRing"
                        />
                    </div>

                    <div v-if="damageQuantity && damageQuantity > 0">
                        <label class="block text-sm font-medium text-gray-700 mb-1">
                            Keterangan Kerusakan <span class="text-red-500">*</span>
                        </label>
                        <textarea
                            v-model="damageNote"
                            @input="errorFinishList.damage_note = null"
                            rows="3"
                            placeholder="Deskripsikan kerusakan..."
                            class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2"
                            :class="[
                                orderTypeTheme.focusRing,
                                errorFinishList.damage_note ? 'errorShake' : ''
                            ]"
                        />
                        <p v-if="errorFinishList.damage_note" class="mt-1 text-xs text-red-500 italic">
                            *{{ errorFinishList.damage_note }}
                        </p>
                    </div>
                </div>

                <div class="flex justify-end gap-x-3">
                    <Button type="tertiary" label="Batal" @click="isFinishModalOpen = false" />
                    <Button label="Selesaikan" full icon="fal fa-check" @click="submitFinish" :loading="isLoadingSubmitFinish" />
                </div>
            </div>
        </div>
    </Teleport>
</template>
